import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { UseFormRegisterReturn } from 'react-hook-form';
import { FieldWrapper, FieldWrapperPassThroughProps } from './FieldWrapper';
import info from '@/assets/info.svg';

const variants = {
  primary: '',
  secondary: 'bg-button',
};

type InputFieldProps = FieldWrapperPassThroughProps & {
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'date'
    | 'month'
    | 'year'
    | 'time'
    | 'file'
    | 'float'
    | 'tel';
  className?: string;
  wrapperClass?: string;
  placeholder?: string;
  registration?: Partial<UseFormRegisterReturn>;
  variant?: keyof typeof variants;
  min?: any;
  disabled?: boolean;
  icon?: string;
  endIcon?: string;
  title?: string;
  value?: string;
  tooltip?: string;
  autoComplete?: string;
  isPassword?: boolean;
};

export const InputField = (props: InputFieldProps) => {
  const {
    type = 'text',
    label,
    variant = 'primary',
    wrapperClass,
    className,
    registration,
    error,
    min,
    disabled = false,
    icon,
    endIcon,
    title,
    value,
    placeholder,
    tooltip,
    autoComplete,
    isPassword = false,
  } = props;

  const [show, setShow] = useState(false);
  // const [nowType, setNowType] = useState(isPassword ? 'password' : type);
  const [nowType, setNowType] = useState(type);

  const isNumeric = type === 'number' || type === 'float';
  const allowsDecimal = type === 'float';
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const [displayValue, setDisplayValue] = useState<string>(value ?? '');

  const formatWithCommas = useMemo(() => {
    return (raw: string) => {
      if (!raw) return '';
      const normalized = raw.replace(/,/g, '');

      if (allowsDecimal) {
        const [intPartRaw, decPartRaw = ''] = normalized.split('.');
        const intPart = intPartRaw.replace(/[^\d]/g, '');
        const decPart = decPartRaw.replace(/[^\d]/g, '');
        const intFormatted = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
        return decPart.length > 0 ? `${intFormatted}.${decPart}` : intFormatted;
      }

      const digits = normalized.replace(/[^\d]/g, '');
      return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };
  }, [allowsDecimal]);

  const toRawNumeric = useMemo(() => {
    return (val: string) => {
      const normalized = (val ?? '').replace(/,/g, '');
      if (allowsDecimal) {
        // keep digits + single dot
        const cleaned = normalized.replace(/[^\d.]/g, '');
        const [a, ...rest] = cleaned.split('.');
        return rest.length ? `${a}.${rest.join('')}` : a;
      }
      return normalized.replace(/[^\d]/g, '');
    };
  }, [allowsDecimal]);

  // Keep display in sync with external value prop if used
  useEffect(() => {
    if (!isNumeric) return;
    if (value === undefined) return;
    const raw = toRawNumeric(value);
    setDisplayValue(formatWithCommas(raw));
    if (hiddenInputRef.current) hiddenInputRef.current.value = raw;
  }, [value, isNumeric, toRawNumeric, formatWithCommas]);

  // Initialize display from RHF default value (uncontrolled)
  useEffect(() => {
    if (!isNumeric) return;
    const raw = hiddenInputRef.current?.value ?? '';
    if (raw) setDisplayValue(formatWithCommas(raw));
  }, [isNumeric, formatWithCommas]);

  return (
    <FieldWrapper className={wrapperClass ?? ''} label={label} error={error}>
      <div className="form-field relative">
        <label htmlFor="input" className="skill-info d-flex align-items-center justify-between">
          <span>
            {label} {icon && <img src={icon} className="reqIcon d-block w-100" />}
          </span>
          {tooltip && (
            <div className="label-tooltip">
              <img src={info} className="info-icon" />
              <span className="tooltip-text">{tooltip}</span>
            </div>
          )}
        </label>
        <div className="input-wrapper relative flex items-center">
          {isNumeric && (
            <input
              type="hidden"
              {...registration}
              ref={(el) => {
                hiddenInputRef.current = el;
                const regRef: any = (registration as any)?.ref;
                if (typeof regRef === 'function') regRef(el);
                else if (regRef) regRef.current = el;
              }}
            />
          )}
          <input
            id="input"
            type={isNumeric ? 'text' : show ? 'text' : nowType}
            className={clsx(
              'form-control',
              error?.message && 'is-invalid',
              variants[variant],
              className
            )}
            disabled={disabled}
            min={min}
            placeholder={placeholder}
            title={title}
            // autoComplete={autoComplete}
            value={isNumeric ? displayValue : value}
            style={{ paddingRight: type === 'password' || endIcon ? '35px' : '0.75rem' }}
            onFocus={(e) => {
              if (isPassword) {
                setNowType('password');
              }
            }}
            inputMode={isNumeric ? 'numeric' : undefined}
            onChange={(e) => {
              if (!isNumeric) {
                registration?.onChange?.(e);
                return;
              }

              const raw = toRawNumeric(e.currentTarget.value);
              const formatted = formatWithCommas(raw);
              setDisplayValue(formatted);
              if (hiddenInputRef.current) hiddenInputRef.current.value = raw;

              const name = (registration as any)?.name;
              registration?.onChange?.({
                ...e,
                target: { ...(e.target as any), name, value: raw },
                currentTarget: { ...(e.currentTarget as any), name, value: raw },
              });
            }}
            onBlur={(e) => {
              registration?.onBlur?.(e);
            }}
            name={isNumeric ? undefined : registration?.name}
            ref={isNumeric ? undefined : (registration as any)?.ref}
          />
          {(isPassword || nowType === 'password') && (
            <span
              role="button"
              tabIndex={-1}
              onKeyDown={() => setShow(!show)}
              onClick={() => setShow(!show)}
              className={clsx('passwordIcon absolute right-3')}
            >
              {!show ? (
                <i className="fa-regular fa-eye" />
              ) : (
                <i className="fa-regular fa-eye-slash" />
              )}
            </span>
          )}
          {endIcon && <img src={endIcon} className="end-icon absolute right-3 w-5 h-5" />}
        </div>
      </div>
    </FieldWrapper>
  );
};
